/**
 * Where each catalogue field sits on Manual Deal Entry (Chris, 5 Oct 2026).
 *
 * The catalogue still has 148 fields. This page shows about 30. Everything
 * else is either in the collapsed More block or only on the deal profile —
 * never deleted from the record, never rewritten.
 */
import type { DealFieldDef } from '@/services/dealFile'

/** Visible section, collapsed More, or profile-only (not on this page). */
export type ManualEntryPlacement =
  | 'deal'
  | 'location'
  | 'size'
  | 'request'
  | 'parties'
  | 'more'
  | 'profile'

export const MANUAL_ENTRY_SECTION_ORDER = [
  'deal',
  'location',
  'size',
  'request',
  'parties',
  'more',
] as const

export type ManualEntrySectionId = (typeof MANUAL_ENTRY_SECTION_ORDER)[number]

export const MANUAL_ENTRY_SECTION_LABELS: Record<ManualEntrySectionId, string> = {
  deal: 'Deal',
  location: 'Location',
  size: 'Size',
  request: 'The Request',
  parties: 'Parties',
  more: 'More',
}

export const MANUAL_ENTRY_DEFAULT_OPEN: Record<string, boolean> = {
  Deal: true,
  Location: true,
  Size: true,
  'The Request': true,
  Parties: true,
  More: false,
}

/** Loan purposes that show Purchase Price. */
export const ACQUISITION_PURPOSES: ReadonlySet<string> = new Set([
  'Acquisition',
  'Acquisition-Construction',
  'Acquisition-Conversion',
])

/** Extra state options Chris asked for. Not US/CA jurisdiction codes. */
export const INTAKE_STATE_EXTRAS = ['XX', 'Multiple locations'] as const

export const INTERNATIONAL_STATE = 'XX'

/**
 * Engine outputs shown read-only. No new derivation direction: each is a forward
 * rule over primary figures (DERIVATION_RULES), and a value keyed here would be a
 * stated figure standing in for the platform's own. `rate_indicative` is the
 * Suggestion the server prices from the dated SOFR print — never typed.
 */
export const CALCULATED_READONLY_FIELDS: ReadonlySet<string> = new Set([
  'debt_yield',
  'debt_yield_stabilized',
  'price_per_unit',
  'price_per_sf',
  'value_per_unit',
  'value_per_sf',
  'loan_per_unit',
  'loan_per_sf',
  'loan_to_purchase_price',
  'annual_debt_service_amortizing',
  'dscr_amortizing',
  'dscr_io_indicative',
  'rate_indicative',
])

/**
 * The in-place half of each request pair, named as one. The catalogue calls it
 * plain "Debt Yield" / "LTV" beside "Debt Yield (Stabilized)" / "LTV
 * (Stabilized)", and on the 2 Nov 2026 run-through Chris read the pair as two
 * Debt Yield Stabilized fields (T-03). The dossier already says "Debt Yield
 * (In-Place)"; the entry page now says the same.
 */
export const MANUAL_ENTRY_LABELS: Readonly<Record<string, string>> = {
  debt_yield: 'Debt Yield (In-Place)',
  ltv: 'LTV (In-Place)',
}

/** A catalogue field as the entry page labels it. */
export function withManualEntryLabel<T extends { name: string; label: string }>(def: T): T {
  const label = MANUAL_ENTRY_LABELS[def.name]
  return label ? { ...def, label } : def
}

export const REQUEST_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['property_value', 'value_stabilized'],
  ['ltv', 'ltv_stabilized'],
  ['noi', 'noi_stabilized'],
  ['debt_yield', 'debt_yield_stabilized'],
]

/**
 * Backshop-required fields that stay visible. Labelled "Needed for Backshop"
 * — never a blocking asterisk.
 */
export const BACKSHOP_HINT_FIELDS: ReadonlySet<string> = new Set([
  'capital_program',
  'unit_of_measure',
])

/**
 * Every catalogue field and where this page puts it.
 *
 * `profile` = keep on the deal profile, hide here. Includes Initial Advance
 * (`initial_funding`): Louis has not approved merging it into Initial Loan
 * Amount, so it stays on the record and off this page.
 */
export const MANUAL_ENTRY_PLACEMENT = {
  property_name: 'deal',
  asset_type: 'deal',
  purpose: 'deal',
  capital_program: 'deal',
  position: 'deal',
  originated: 'deal',
  stage: 'deal',
  control_id: 'profile',
  loan_type: 'profile',

  street: 'location',
  city: 'location',
  state: 'location',
  zip: 'location',
  country: 'location',
  msa: 'profile',
  location: 'profile',
  units_or_sf: 'profile',
  year_built: 'profile',
  year_renovated: 'profile',
  renovated_units: 'profile',
  occupancy: 'profile',
  property_brand: 'profile',
  property_management: 'profile',
  parcel_id: 'profile',
  site_area: 'profile',
  building_area: 'profile',
  stories: 'profile',
  parking_spaces: 'profile',
  avg_lease_term: 'profile',
  zoning: 'profile',
  county: 'profile',
  // DER-04: filled from the asset-level coordinate by the county lookup, never keyed here.
  county_fips: 'profile',
  property_description: 'profile',
  property_comment: 'profile',
  website: 'profile',

  unit_of_measure: 'size',
  units: 'profile',
  sf: 'profile',
  property_subtype: 'size',
  tenure: 'size',

  currency: 'request',
  loan_amount: 'request',
  future_funding: 'request',
  fully_funded_amount: 'request',
  purchase_price: 'request',
  total_cost: 'request',
  property_value: 'request',
  value_stabilized: 'request',
  ltv: 'request',
  ltv_stabilized: 'request',
  noi: 'request',
  noi_stabilized: 'request',
  debt_yield: 'request',
  debt_yield_stabilized: 'request',
  price_per_unit: 'request',
  price_per_sf: 'request',
  initial_funding: 'profile',
  // DER-06/07: the value-basis and loan-basis per-unit figures and the loan-to-price
  // ratio, all engine outputs — in More, never keyed.
  value_per_unit: 'more',
  value_per_sf: 'more',
  loan_per_unit: 'more',
  loan_per_sf: 'more',
  loan_to_purchase_price: 'more',

  ncf: 'more',
  cap_rate: 'more',
  exit_cap: 'more',
  ltc: 'more',
  dscr: 'more',
  dscr_stabilized: 'more',
  cap_rate_stabilized: 'more',
  breakeven_occupancy: 'more',
  gross_revenue: 'more',
  operating_expenses: 'more',
  expense_ratio: 'more',
  annual_debt_service: 'more',
  base_rent_annual: 'more',
  rent_per_sf: 'more',
  ebitda: 'more',
  gop: 'more',
  departmental_profit: 'more',
  undistributed_expenses: 'more',
  fixed_expenses: 'more',
  reserve: 'more',
  adr: 'more',
  revpar: 'more',
  room_nights: 'more',
  day_count: 'more',
  dscr_io: 'more',
  dscr_io_stabilized: 'more',
  dscr_stated: 'more',
  cap_rate_stated: 'more',
  property_value_stated: 'more',
  noi_stated: 'more',
  debt_yield_stated: 'more',
  dscr_hotel_after_ffe: 'more',
  // DER-01/09/10: amortizing and indicative coverage (engine outputs), and the rent roll's
  // gross potential rent (an input to breakeven occupancy, keyed like base rent).
  annual_debt_service_amortizing: 'more',
  dscr_amortizing: 'more',
  dscr_io_indicative: 'more',
  gross_potential_rent: 'more',

  total_equity: 'more',
  lp_equity: 'more',
  gp_equity: 'more',
  peak_equity: 'more',
  levered_irr: 'more',
  cash_on_cash: 'more',
  unlevered_irr: 'more',
  equity_multiple: 'more',
  preferred_return: 'more',
  irr_hurdle: 'more',
  promote: 'more',
  yield_on_cost: 'more',
  sale_proceeds: 'more',
  disposition_fee: 'more',
  exit_year: 'more',
  interest_reserve: 'more',
  refi_proceeds: 'more',
  refi_dscr: 'more',

  rate_type: 'more',
  index: 'more',
  payment_frequency: 'more',
  lien_position: 'more',
  amortization_type: 'more',
  spread: 'more',
  floor: 'more',
  rate: 'more',
  rate_indicative: 'more',
  term_months: 'more',
  extensions: 'more',
  extension_count: 'more',
  io_months: 'more',
  amortization_months: 'more',
  closing_date: 'more',
  maturity_date: 'more',
  extension_maturity_date: 'more',
  note_name: 'more',
  interest_rate_locked: 'more',
  recourse: 'more',
  origination_fee_bps: 'more',
  exit_fee_bps: 'more',
  prepayment: 'more',
  covenant_min_dscr: 'more',
  covenant_max_ltv: 'more',
  covenant_min_debt_yield: 'more',
  // EXT-13: the sizing constraints a term sheet states, kept beside the covenants.
  ltc_max: 'more',
  ltv_stabilized_max: 'more',
  debt_yield_stabilized_min: 'more',

  sponsor: 'parties',
  brokerage_company: 'parties',
  broker_name: 'parties',
  originator: 'parties',
  underwriter: 'parties',
  secondary_underwriter: 'parties',
  analyst: 'parties',
  assignee: 'parties',
  guarantor: 'more',
  principal: 'more',
  borrowing_entity: 'more',
  sponsor_contact: 'more',
  broker_contact: 'more',
  broker_phone: 'more',
  internal_contact: 'more',
  internal_contact_email: 'more',
  investor: 'more',

  deal_comment: 'more',
  priority: 'more',
  target_close: 'more',
  // The desk's clock dates (Chris Young, 26 Oct 2026; CY-09/10): set on the deal
  // when the quote letter goes out and when the deposit wire lands, never at intake.
  quote_letter_date: 'profile',
  deposit_wire_date: 'profile',
} as const satisfies Record<string, ManualEntryPlacement>

export type CatalogueFieldName = keyof typeof MANUAL_ENTRY_PLACEMENT

export function placementOf(name: string): ManualEntryPlacement | undefined {
  return MANUAL_ENTRY_PLACEMENT[name as CatalogueFieldName]
}

export function fieldsInSection(
  fields: DealFieldDef[],
  section: ManualEntrySectionId,
): DealFieldDef[] {
  const wanted = new Set(
    Object.entries(MANUAL_ENTRY_PLACEMENT)
      .filter(([, place]) => place === section)
      .map(([name]) => name),
  )
  return fields.filter((f) => wanted.has(f.name))
}

/** State dropdown: catalogue list plus XX and Multiple locations. */
export function intakeStateOptions(options: readonly string[]): string[] {
  const seen = new Set(options)
  const extra = INTAKE_STATE_EXTRAS.filter((code) => !seen.has(code))
  return extra.length ? [...options, ...extra] : [...options]
}

export function showsPurchasePrice(purpose: string | undefined): boolean {
  return ACQUISITION_PURPOSES.has((purpose || '').trim())
}

export function showsCountry(state: string | undefined): boolean {
  return (state || '').trim().toUpperCase() === INTERNATIONAL_STATE
}

export function pairMate(name: string): string | undefined {
  for (const [left, right] of REQUEST_PAIRS) {
    if (name === left) return right
    if (name === right) return left
  }
  return undefined
}

export function isPairLeft(name: string): boolean {
  return REQUEST_PAIRS.some(([left]) => left === name)
}

/** Hide a field on this page given the current values and who is looking. */
export function hideOnForm(
  name: string,
  values: Record<string, string>,
  isAdmin: boolean,
): boolean {
  if (name === 'stage' && !isAdmin) return true
  if (name === 'country' && !showsCountry(values.state)) return true
  if (name === 'purchase_price' && !showsPurchasePrice(values.purpose)) return true
  return false
}

export function sectionDomId(label: string): string {
  return `manual-entry-section-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}
